import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { NewProjectModelIcon } from '../../../../../assets/images';
import BaiscModel from '../../../../../components/action-modal';
import { ProjectModalProps } from '../../automator.props';
import { requiredText, savingText } from '../../data/constants';
import { createNewWorkflowProject, editExistingWorkflowProject } from '../../services';
import makeToast from '../../../../../components/toaster';
import { validateTextAreaField, validateTextField } from '../../../../../services';

function NewProjectModal({ open, setNewProjectModal, data, setDataUpdated }: ProjectModalProps) {
  const navigate = useNavigate();
  const [values, setValues] = useState<{ name: string; desc: string }>({
    name: '',
    desc: '',
  });
  const [error, setError] = useState<{ name: string; desc: string }>({
    name: '',
    desc: '',
  });
  const [saving, setSaving] = useState<string>('Save');

  useEffect(() => {
    if (data?.name) {
      setValues({ name: data.name, desc: data?.description });
    }
  }, [open]);

  const handleResetData = () => {
    setNewProjectModal(false);
    setError((prev) => ({ ...prev, name: '', desc: '' }));
    setValues({ name: '', desc: '' });
    setSaving('Save');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const projectNameError =
      e?.target?.name === 'name' && validateTextField(e.target.value, 'Project Name');
    const projectDescError =
      e?.target?.name === 'desc' && validateTextAreaField(e.target.value, 'Project Description');
    setValues((prev) => ({
      ...prev,
      [e?.target?.name]: e.target.value,
    }));
    setError((prev) => ({
      ...prev,
      [e?.target?.name]: '',
      ...(e?.target?.name === 'name' && projectNameError && { name: projectNameError }),
      ...(e?.target?.name === 'desc' && projectDescError && { desc: projectDescError }),
    }));
  };

  const handleAutomator = () => {
    const projectNameError = validateTextField(values.name, 'Project Name');
    const projectDescError = validateTextAreaField(values.desc, 'Project Description');

    // Check for validation errors
    if (!values.name || projectDescError || projectNameError) {
      setError((prev) => ({
        ...prev,
        ...(!values.name ? { name: requiredText } : projectNameError && { name: projectNameError }),
        ...(!values.desc ? null : projectDescError && { desc: projectDescError }),
      }));
      return;
    }

    // Check if the values have changed
    const hasChanges = values.name !== data?.name || values.desc !== data?.description;

    if (!hasChanges) {
      // If no changes, navigate directly
      setNewProjectModal(false);
      navigate(`/automator/workflow/${data?.id}/${data?.workflow_id}`);
      return;
    }

    // Proceed with API call if values have changed
    setSaving(savingText);
    const request = data?.name ? editExistingWorkflowProject : createNewWorkflowProject;

    request({ name: values.name, description: values.desc }, data?.id)
      .then((res) => {
        if (res?.status === 201 || res?.status === 200) {
          const projectId = res?.data?.data?.id;
          makeToast(
            'success',
            data?.name ? 'Project updated successfully!' : 'Project created successfully!',
          );
          setDataUpdated?.((prev: boolean) => !prev);
          setNewProjectModal(false);
          if (projectId) {
            navigate(`/automator/workflow/${projectId}`);
          } else {
            navigate(`/automator/workflow`);
          }
        }
      })
      .catch((err) => {
        makeToast('error', err?.response?.data?.message || 'Something went wrong!');
      })
      .finally(() => {
        setSaving('Save');
      });
  };

  return (
    <BaiscModel
      open={open}
      confirmButtonText={saving}
      className="w-[480px]"
      modalTitle={data?.name && data?.description ? 'Edit Project' : 'New Project'}
      modalDesc={`${
        data?.name && data?.description
          ? 'Edit project to the system'
          : 'Create a new project to the system'
      }`}
      modalLogo={NewProjectModelIcon}
      resetData={handleResetData}
      saveData={handleAutomator}
    >
      <div className="w-full">
        <div className="w-full">
          <div className="flex flex-col text-sm p-2 w-full">
            <span className="text-[#344054] font-medium">
              Project Name
              <span className="text-red-600">*</span>
            </span>
            <input
              placeholder="Enter Project Name"
              className={`mt-1 py-[5px] text-sm ${
                error.name ? 'focus:border-red-600 border-red-600' : 'border-[#E4E4E7]'
              }  rounded-lg w-full`}
              onChange={(e) => handleChange(e)}
              required
              name="name"
              value={values.name}
            />
            <span className="text-red-600">{error.name}</span>
          </div>
          <div className="flex flex-col text-sm p-2 w-full">
            <span className="text-[#344054] font-medium">Project Description</span>
            <textarea
              placeholder="Enter Project Description"
              className={`mt-1 text-sm py-[5px] ${
                error.desc ? 'focus:border-red-600 border-red-600' : 'border-[#E4E4E7]'
              } rounded-lg w-full`}
              onChange={(e) => {
                handleChange(e);
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              required
              rows={2}
              name="desc"
              value={values.desc}
            />
            <span className="text-red-600">{error.desc}</span>
          </div>
        </div>
      </div>
    </BaiscModel>
  );
}

export default NewProjectModal;
